import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Save, Upload, Smartphone, Monitor, Apple, Link as LinkIcon, Package, Copy } from "lucide-react";
import { api } from "@/lib/api";
import { PageTitle, Card, Field, inputCls, GoldButton, GhostButton } from "@/admin/ui";

const AndroidGlyph = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M17.6 9.48l1.84-3.18a.4.4 0 0 0-.15-.55.4.4 0 0 0-.55.14l-1.86 3.22a11.4 11.4 0 0 0-9.76 0L5.26 5.9a.4.4 0 0 0-.7.4l1.84 3.19A10.86 10.86 0 0 0 1 18h22a10.86 10.86 0 0 0-5.4-8.52ZM7 15.25a1 1 0 1 1 1-1 1 1 0 0 1-1 1Zm10 0a1 1 0 1 1 1-1 1 1 0 0 1-1 1Z" />
  </svg>
);
const WindowsGlyph = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M3 5.5 10.7 4.4v7.6H3V5.5Zm0 7.9h7.7V21L3 19.9v-6.5Zm8.6-9.2L21 3v9.1h-9.4V4.2Zm0 9.2H21V21l-9.4-1.4v-6.2Z" />
  </svg>
);

const PLATFORMS = [
  { key: "android", label: "Android APK", icon: AndroidGlyph, iconColor: "#00E676", accept: ".apk,application/vnd.android.package-archive", urlField: "app_android_url", enField: "app_android_enabled" },
  { key: "windows", label: "Windows (.EXE)", icon: WindowsGlyph, iconColor: "#00A8FF", accept: ".exe,.msi", urlField: "app_windows_url", enField: "app_windows_enabled" },
  { key: "macos", label: "macOS (.DMG)", icon: Apple, iconColor: "#e5e7eb", accept: ".dmg,.pkg", urlField: "app_macos_url", enField: "app_macos_enabled" },
  { key: "google_play", label: "Google Play", icon: Smartphone, iconColor: "#00E676", accept: "", urlField: "app_google_play_url", enField: "app_google_play_enabled", linkOnly: true },
  { key: "apple_store", label: "App Store", icon: Apple, iconColor: "#e5e7eb", accept: "", urlField: "app_apple_store_url", enField: "app_apple_store_enabled", linkOnly: true },
];

const textareaCls =
  "w-full bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#F5C542]/50 transition-colors font-mono leading-relaxed resize-y";

export const AppManager = () => {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState({});

  useEffect(() => { api.get("/admin/settings").then(({ data }) => setS(data)); }, []);

  const set = (patch) => setS((v) => ({ ...v, ...patch }));

  const save = async () => {
    setSaving(true);
    try {
      const { key, _id, ...payload } = s;
      await api.put("/admin/settings", payload);
      toast.success("Configurações do aplicativo salvas");
    } catch { toast.error("Erro ao salvar"); }
    finally { setSaving(false); }
  };

  const doUpload = async (platformKey, file) => {
    if (!file) return;
    setUploading((u) => ({ ...u, [platformKey]: true }));
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/admin/files", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const platform = PLATFORMS.find((p) => p.key === platformKey);
      const url = data.url;
      set({ [platform.urlField]: url });
      toast.success(`Arquivo enviado (${(data.size / 1024 / 1024).toFixed(1)} MB)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha no upload");
    } finally {
      setUploading((u) => ({ ...u, [platformKey]: false }));
    }
  };

  const copyUrl = async (url) => {
    if (!url) return;
    try {
      const abs = url.startsWith("http") ? url : `${window.location.origin}${url}`;
      await navigator.clipboard.writeText(abs);
      toast.success("URL copiada");
    } catch { toast.error("Falha ao copiar"); }
  };

  if (!s) return <div className="text-white/40">Carregando...</div>;

  return (
    <div data-testid="admin-app-manager">
      <PageTitle
        title="Gerenciamento do Aplicativo"
        subtitle="Upload de binários, links das lojas, versão e disponibilidade por plataforma"
        action={<GoldButton onClick={save} disabled={saving} data-testid="app-save"><Save className="w-4 h-4 inline mr-1" /> {saving ? "Salvando..." : "Salvar"}</GoldButton>}
      />

      {/* Version + Changelog */}
      <Card className="mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Package className="w-4 h-4 text-[#F5C542]" />
          <h3 className="font-display font-bold text-white">Versão & Changelog</h3>
        </div>
        <div className="grid sm:grid-cols-[200px_1fr] gap-4">
          <Field label="Versão">
            <input
              value={s.app_version ?? ""}
              onChange={(e) => set({ app_version: e.target.value })}
              className={inputCls}
              placeholder="1.0.0"
              data-testid="app-version"
            />
          </Field>
          <Field label="Descrição da atualização (opcional)">
            <textarea
              rows={3}
              value={s.app_changelog ?? ""}
              onChange={(e) => set({ app_changelog: e.target.value })}
              className={textareaCls}
              placeholder="Melhorias de performance, novos sinais, correções..."
              data-testid="app-changelog"
            />
          </Field>
        </div>
      </Card>

      {/* PWA */}
      <Card className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className="w-8 h-8 rounded-lg flex items-center justify-center"
                  style={{ background: "rgba(245, 197, 66,0.15)", border: "1px solid rgba(245, 197, 66,0.4)" }}>
              <Smartphone className="w-4 h-4 text-[#F5C542]" />
            </span>
            <h3 className="font-display font-bold text-white">PWA — Aplicativo Web</h3>
          </div>
          <label className="inline-flex items-center gap-2 text-sm text-white/70">
            <input type="checkbox"
                   checked={s.app_pwa_enabled !== false}
                   onChange={(e) => set({ app_pwa_enabled: e.target.checked })}
                   data-testid="app-pwa-enabled"
                   className="w-4 h-4 accent-[#F5C542]" />
            Disponível
          </label>
        </div>
        <div className="text-xs text-white/50 leading-relaxed">
          O PWA é <b className="text-white/80">instalável direto do navegador</b> em Android, iOS ("Adicionar à Tela de Início"),
          Windows e macOS. Ícone e splash screen já configurados via <code className="text-[#F5C542]">/manifest.json</code>.
          Nada mais precisa ser feito.
        </div>
      </Card>

      {/* Platforms */}
      <div className="grid md:grid-cols-2 gap-6">
        {PLATFORMS.map((p) => {
          const url = s[p.urlField] || "";
          const en = s[p.enField] !== false;
          const busy = !!uploading[p.key];
          const Icon = p.icon;
          return (
            <Card key={p.key} data-testid={`app-platform-${p.key}`}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <span className="w-8 h-8 rounded-lg flex items-center justify-center"
                        style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.08)" }}>
                    <Icon className="w-4 h-4" style={{ color: p.iconColor }} />
                  </span>
                  <h3 className="font-display font-bold text-white">{p.label}</h3>
                </div>
                <label className="inline-flex items-center gap-2 text-sm text-white/70">
                  <input type="checkbox"
                         checked={en}
                         onChange={(e) => set({ [p.enField]: e.target.checked })}
                         className="w-4 h-4 accent-[#F5C542]"
                         data-testid={`app-en-${p.key}`} />
                  Disponível
                </label>
              </div>

              <Field label={p.linkOnly ? "URL da loja" : "URL do arquivo (auto-preenchida ao fazer upload)"}>
                <div className="flex gap-2">
                  <input value={url}
                         onChange={(e) => set({ [p.urlField]: e.target.value })}
                         className={inputCls}
                         placeholder={p.linkOnly ? "https://..." : "/api/files/... ou URL externa"}
                         data-testid={`app-url-${p.key}`} />
                  {url && (
                    <button
                      onClick={() => copyUrl(url)}
                      className="shrink-0 w-11 h-11 rounded-xl bg-white/[0.03] border border-white/10 hover:border-white/25 text-white/70 hover:text-white flex items-center justify-center transition-colors"
                      title="Copiar URL"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </Field>

              {!p.linkOnly && (
                <div className="mt-3">
                  <UploadField
                    accept={p.accept}
                    disabled={busy}
                    onFile={(f) => doUpload(p.key, f)}
                    testid={`app-upload-${p.key}`}
                    label={busy ? "Enviando..." : `Enviar arquivo (${p.accept.replace(/,/g, " / ")})`}
                  />
                </div>
              )}

              {p.linkOnly && (
                <div className="mt-1 text-[11px] text-white/40 flex items-center gap-1">
                  <LinkIcon className="w-3 h-3" /> Cole o link da loja oficial. Vazio = "Em breve".
                </div>
              )}
            </Card>
          );
        })}
      </div>

      <div className="mt-6 flex justify-end">
        <GoldButton onClick={save} disabled={saving} data-testid="app-save-bottom">
          <Save className="w-4 h-4 inline mr-1" /> {saving ? "Salvando..." : "Salvar alterações"}
        </GoldButton>
      </div>
    </div>
  );
};

const UploadField = ({ accept, disabled, onFile, label, testid }) => {
  const ref = useRef(null);
  return (
    <div>
      <input
        ref={ref}
        type="file"
        accept={accept || undefined}
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onFile(f);
          if (ref.current) ref.current.value = "";
        }}
        data-testid={testid}
      />
      <button
        type="button"
        onClick={() => ref.current?.click()}
        disabled={disabled}
        className="w-full inline-flex items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-bold text-white/90 hover:text-white bg-white/[0.03] border border-white/10 hover:border-[#F5C542]/50 transition-colors disabled:opacity-60 disabled:cursor-wait"
      >
        <Upload className="w-4 h-4" /> {label}
      </button>
    </div>
  );
};
